// Persian letter shapes data for tracing practice
export interface LetterShape {
  letter: string;
  name: string;
  pronunciation: string;
  forms: {
    isolated: string; // SVG path for isolated form
    initial: string;  // SVG path for initial form
    medial: string;    // SVG path for medial form
    final: string;    // SVG path for final form
  };
  strokeOrder: string[]; // Array of stroke paths in order
  difficulty: 'easy' | 'medium' | 'hard';
}

export const persianLetterShapes: LetterShape[] = [
  {
    letter: 'آ',
    name: 'Alef',
    pronunciation: 'alef',
    forms: {
      isolated: 'M 50 20 L 50 160 M 40 20 L 60 20',
      initial: 'M 50 20 L 50 160 M 40 20 L 60 20',
      medial: 'M 50 20 L 50 160 M 40 20 L 60 20',
      final: 'M 50 20 L 50 160 M 40 20 L 60 20'
    },
    strokeOrder: [
      'M 50 20 L 50 160', // Vertical line
      'M 40 20 L 60 20'  // Top horizontal line
    ],
    difficulty: 'easy'
  },
  {
    letter: 'ب',
    name: 'Be',
    pronunciation: 'be',
    forms: {
      isolated: 'M 50 40 Q 50 20 70 40 Q 90 60 70 80 Q 50 100 50 120 L 50 160',
      initial: 'M 50 40 Q 50 20 70 40 Q 90 60 70 80 Q 50 100 50 120 L 50 160',
      medial: 'M 50 80 Q 50 60 70 80 Q 90 100 70 120 Q 50 140 50 160',
      final: 'M 50 40 Q 50 20 70 40 Q 90 60 70 80 Q 50 100 50 120 L 50 160'
    },
    strokeOrder: [
      'M 50 40 Q 50 20 70 40 Q 90 60 70 80 Q 50 100 50 120', // Curved part
      'L 50 160' // Vertical line
    ],
    difficulty: 'medium'
  },
  {
    letter: 'پ',
    name: 'Pe',
    pronunciation: 'pe',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80', // Vertical line
      'M 15 15 L 25 15' // Top dots
    ],
    difficulty: 'medium'
  },
  {
    letter: 'ت',
    name: 'Te',
    pronunciation: 'te',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15 M 15 25 L 25 25',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15 M 15 25 L 25 25',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35 M 15 45 L 25 45',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15 M 15 25 L 25 25'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80', // Vertical line
      'M 15 15 L 25 15', // Top dots
      'M 15 25 L 25 25' // Bottom dots
    ],
    difficulty: 'medium'
  },
  {
    letter: 'ث',
    name: 'Se',
    pronunciation: 'se',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15 M 15 25 L 25 25 M 15 35 L 25 35',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15 M 15 25 L 25 25 M 15 35 L 25 35',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35 M 15 45 L 25 45 M 15 55 L 25 55',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15 M 15 25 L 25 25 M 15 35 L 25 35'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80', // Vertical line
      'M 15 15 L 25 15', // Top dots
      'M 15 25 L 25 25', // Middle dots
      'M 15 35 L 25 35' // Bottom dots
    ],
    difficulty: 'hard'
  },
  {
    letter: 'ج',
    name: 'Jim',
    pronunciation: 'jim',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80', // Vertical line
      'M 15 15 L 25 15' // Dot
    ],
    difficulty: 'medium'
  },
  {
    letter: 'چ',
    name: 'Che',
    pronunciation: 'che',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15 M 15 25 L 25 25',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15 M 15 25 L 25 25',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35 M 15 45 L 25 45',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15 M 15 25 L 25 25'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80', // Vertical line
      'M 15 15 L 25 15', // Top dots
      'M 15 25 L 25 25' // Bottom dots
    ],
    difficulty: 'medium'
  },
  {
    letter: 'ح',
    name: 'He',
    pronunciation: 'he',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80' // Vertical line
    ],
    difficulty: 'easy'
  },
  {
    letter: 'خ',
    name: 'Khe',
    pronunciation: 'khe',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80', // Vertical line
      'M 15 15 L 25 15' // Dot
    ],
    difficulty: 'medium'
  },
  {
    letter: 'د',
    name: 'Dal',
    pronunciation: 'dal',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60' // Single curved stroke
    ],
    difficulty: 'easy'
  },
  {
    letter: 'ذ',
    name: 'Zal',
    pronunciation: 'zal',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 M 15 15 L 25 15',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 M 15 15 L 25 15',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 M 15 15 L 25 15'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'M 15 15 L 25 15' // Dot
    ],
    difficulty: 'easy'
  },
  {
    letter: 'ر',
    name: 'Re',
    pronunciation: 're',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60' // Single curved stroke
    ],
    difficulty: 'easy'
  },
  {
    letter: 'ز',
    name: 'Ze',
    pronunciation: 'ze',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 M 15 15 L 25 15',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 M 15 15 L 25 15',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 M 15 15 L 25 15'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'M 15 15 L 25 15' // Dot
    ],
    difficulty: 'easy'
  },
  {
    letter: 'ژ',
    name: 'Zhe',
    pronunciation: 'zhe',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 M 15 15 L 25 15 M 15 25 L 25 25',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 M 15 15 L 25 15 M 15 25 L 25 25',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35 M 15 45 L 25 45',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 M 15 15 L 25 15 M 15 25 L 25 25'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'M 15 15 L 25 15', // Top dots
      'M 15 25 L 25 25' // Bottom dots
    ],
    difficulty: 'medium'
  },
  {
    letter: 'س',
    name: 'Sin',
    pronunciation: 'sin',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80' // Vertical line
    ],
    difficulty: 'easy'
  },
  {
    letter: 'ش',
    name: 'Shin',
    pronunciation: 'shin',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80', // Vertical line
      'M 15 15 L 25 15' // Dot
    ],
    difficulty: 'medium'
  },
  // Add more letters to ensure comprehensive coverage
  {
    letter: 'ص',
    name: 'Sad',
    pronunciation: 'sad',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80' // Vertical line
    ],
    difficulty: 'easy'
  },
  {
    letter: 'ض',
    name: 'Zad',
    pronunciation: 'zad',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80', // Vertical line
      'M 15 15 L 25 15' // Dot
    ],
    difficulty: 'medium'
  },
  {
    letter: 'ط',
    name: 'Ta',
    pronunciation: 'ta',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80' // Vertical line
    ],
    difficulty: 'easy'
  },
  {
    letter: 'ظ',
    name: 'Za',
    pronunciation: 'za',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80', // Vertical line
      'M 15 15 L 25 15' // Dot
    ],
    difficulty: 'medium'
  },
  {
    letter: 'ع',
    name: 'Ain',
    pronunciation: 'ain',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80' // Vertical line
    ],
    difficulty: 'easy'
  },
  {
    letter: 'غ',
    name: 'Ghain',
    pronunciation: 'ghain',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80', // Vertical line
      'M 15 15 L 25 15' // Dot
    ],
    difficulty: 'medium'
  },
  {
    letter: 'ف',
    name: 'Fe',
    pronunciation: 'fe',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80' // Vertical line
    ],
    difficulty: 'easy'
  },
  {
    letter: 'ق',
    name: 'Qaf',
    pronunciation: 'qaf',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80', // Vertical line
      'M 15 15 L 25 15' // Dot
    ],
    difficulty: 'medium'
  },
  {
    letter: 'ک',
    name: 'Kaf',
    pronunciation: 'kaf',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80' // Vertical line
    ],
    difficulty: 'easy'
  },
  {
    letter: 'گ',
    name: 'Gaf',
    pronunciation: 'gaf',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80', // Vertical line
      'M 15 15 L 25 15' // Dot
    ],
    difficulty: 'medium'
  },
  {
    letter: 'ل',
    name: 'Lam',
    pronunciation: 'lam',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80' // Vertical line
    ],
    difficulty: 'easy'
  },
  {
    letter: 'م',
    name: 'Mim',
    pronunciation: 'mim',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80' // Vertical line
    ],
    difficulty: 'easy'
  },
  {
    letter: 'ن',
    name: 'Nun',
    pronunciation: 'nun',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80' // Vertical line
    ],
    difficulty: 'easy'
  },
  {
    letter: 'و',
    name: 'Vav',
    pronunciation: 'vav',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60' // Single curved stroke
    ],
    difficulty: 'easy'
  },
  {
    letter: 'ه',
    name: 'He',
    pronunciation: 'he',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80' // Vertical line
    ],
    difficulty: 'easy'
  },
  {
    letter: 'ی',
    name: 'Ye',
    pronunciation: 'ye',
    forms: {
      isolated: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      initial: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15',
      medial: 'M 20 40 Q 20 30 30 40 Q 40 50 30 60 Q 20 70 20 80 M 15 35 L 25 35',
      final: 'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60 L 20 80 M 15 15 L 25 15'
    },
    strokeOrder: [
      'M 20 20 Q 20 10 30 20 Q 40 30 30 40 Q 20 50 20 60', // Curved part
      'L 20 80', // Vertical line
      'M 15 15 L 25 15' // Dot
    ],
    difficulty: 'medium'
  }
];

// Helper function to get letter shape by letter
export const getLetterShape = (letter: string): LetterShape | undefined => {
  return persianLetterShapes.find(shape => shape.letter === letter);
};

// Helper function to get all letters by difficulty
export const getLettersByDifficulty = (difficulty: 'easy' | 'medium' | 'hard'): LetterShape[] => {
  return persianLetterShapes.filter(shape => shape.difficulty === difficulty);
};
